import { createContext, useContext, useState, type ReactNode } from "react";
import { Button } from "@noiman/ui/button";
import { TRIAL_CONSULT_URL } from "@/lib/luagate/trial-lock";
import { HandIcon } from "./HandIcon";
import { LgModal } from "./LgModal";
import "./lg-members-only.css";

/*
体験の人に「見せるけれど使えない」物の演出 (2026-10-15 代表「体験の人には色々見せて OK。ただ受講生のみの鍵の演出にする」)。
錠の絵と「受講生のみ」の札を付け、押すと小さな窓で 1 文と入会の相談への道を出す。
鍵の物は hover で変わらず、focus の輪は出す。押せる高さは 44px 以上。黄 (次に押すもの) は使わない
*/

export const MEMBERS_ONLY_LABEL = "受講生のみ";

/** 「受講の相談をする」の行き先。既定は体験の人の相談の頁。ログイン無しのデモ (/demo) は HP の受講の案内に替える */
export const LgConsultHrefContext = createContext<string>(TRIAL_CONSULT_URL);
const MEMBERS_ONLY_SENTENCE = "この先はゆめさくエンジニアの受講生だけが使えます。";

/** 錠の絵と「受講生のみ」の札。行の行末や題の横に置く (押せない飾り)。label で札の字を替えられる (実プロダクト開発演習パスは「演習の参加者のみ」) */
export function LgMembersOnlyBadge({ className, label = MEMBERS_ONLY_LABEL }: { className?: string; label?: string }) {
  return (
    <span className={`lg-mo-badge${className ? ` ${className}` : ""}`}>
      <HandIcon name="state-locked" size={20} className="lg-mo-badge__lock" />
      <span className="lg-mo-badge__tag">{label}</span>
    </span>
  );
}

/** 錠の絵だけ (札は付けない)。同じ並びの頭に LgMembersOnlyBadge を 1 つ置いたときの行末に使う。読み上げは「受講生のみ」 */
export function LgMembersOnlyLock({ className, label = MEMBERS_ONLY_LABEL }: { className?: string; label?: string }) {
  return (
    <span className={`lg-mo-badge${className ? ` ${className}` : ""}`}>
      <HandIcon name="state-locked" size={20} className="lg-mo-badge__lock" />
      <span className="lg-sr-only">{label}</span>
    </span>
  );
}

/** 「受講生のみ」の窓。1 文と、体験の人の次の一歩 (入会の相談) */
export function LgMembersOnlyDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const consultHref = useContext(LgConsultHrefContext);
  return (
    <LgModal open={open} onClose={onClose} title={MEMBERS_ONLY_LABEL} size="sm">
      <div className="lg-mo-dialog">
        <p>{MEMBERS_ONLY_SENTENCE}</p>
        <p>
          <a className="lg-mo-dialog__link" href={consultHref} target="_blank" rel="noopener noreferrer">
            受講の相談をする<span className="lg-sr-only">(新しいタブで開きます)</span>
          </a>
        </p>
      </div>
    </LgModal>
  );
}

/**
 * 使えない操作 (開く、提出する、動かす、再生する、予約する) の代わりに置くボタン。
 * 押すと「受講生のみ」の窓が開く。label は元の操作の名前 (「1-1 を開く」など)
 */
export function LgMembersOnlyButton({ label, className, tag = true }: { label: ReactNode; className?: string; /** 「受講生のみ」の札をボタンの中にも付けるか。すぐそばに札があるときは false */ tag?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="ghost" size="sm" type="button" className={`lg-mo-btn${className ? ` ${className}` : ""}`} aria-haspopup="dialog" onClick={() => setOpen(true)}>
        <HandIcon name="state-locked" size={20} className="lg-mo-badge__lock" />
        <span className="lg-mo-btn__label">{label}</span>
        {tag ? <span className="lg-mo-badge__tag">{MEMBERS_ONLY_LABEL}</span> : null}
      </Button>
      <LgMembersOnlyDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/**
 * 本文の下を覆う薄い地の帯。上に見せた頭の続きは受講生だけ、と伝え、受講生になる道 (入会の相談) を直に置く。
 * 押しても窓が開くだけの「続きを読む」の釦は置かない (読めるように見えて読めない)。ぼかしと影は使わない
 */
export function LgMembersOnlyCover({
  children,
  label = MEMBERS_ONLY_LABEL,
  link,
  className,
}: {
  children?: ReactNode;
  /** 札の字。既定は「受講生のみ」 */
  label?: string;
  /** 帯の釦。既定は体験の人の「受講の相談をする」(新しいタブ)。同じサイトの頁へ送るときは href と字を渡す (同じタブで開く) */
  link?: { href: string; text: string };
  className?: string;
}) {
  const consultHref = useContext(LgConsultHrefContext);
  return (
    <section className={`lg-mo-cover${className ? ` ${className}` : ""}`} aria-label={link ? label : `${label}の続き`}>
      <div className="lg-mo-cover__body">
        <LgMembersOnlyBadge label={label} />
        <p className="lg-mo-cover__text">{children ?? "この続きと演習は、受講生になると読めます。"}</p>
        {link ? (
          <a className="lg-mo-cover__link" href={link.href}>
            {link.text}
          </a>
        ) : (
          <a className="lg-mo-cover__link" href={consultHref} target="_blank" rel="noopener noreferrer">
            受講の相談をする<span className="lg-sr-only">(新しいタブで開きます)</span>
          </a>
        )}
      </div>
    </section>
  );
}
